import Dexie from 'dexie';

import db from './db';
import { logError } from './logger';
import { isMessagePreviewCandidate } from './messagePreview';

const WORKSPACE_LATEST_MESSAGE_LIMIT = 10;
export const WORKSPACE_LATEST_MESSAGES_REFRESH_EVENT = 'output:internal:live-journal:refresh-workspace-latest-messages';

function previewRow(message) {
  if (!isMessagePreviewCandidate(message) || !message.id || !message.workspace_id || !message.channel_id) return null;

  return {
    id: String(message.id),
    workspace_id: String(message.workspace_id),
    channel_id: String(message.channel_id),
    sort_order: message.sort_order,
    timestamp: message.timestamp,
    author_id: message.author_id,
    author_type: message.author_type,
    text: message.text,
    type: message.type,
    data: message.data,
    actors: message.actors,
    attachment_count: Array.isArray(message.attachments) ? message.attachments.length : message.attachment_count || 0,
    contains_only_attachments: message.contains_only_attachments,
  };
}

function newestFirstForWorkspace(table, workspaceId) {
  return table.where('[workspace_id+sort_order]').between([workspaceId, Dexie.minKey], [workspaceId, Dexie.maxKey], false, true).reverse();
}

async function trimWorkspace(workspaceId) {
  const staleIds = await newestFirstForWorkspace(db.workspace_latest_messages, workspaceId)
    .offset(WORKSPACE_LATEST_MESSAGE_LIMIT)
    .primaryKeys();

  if (staleIds.length > 0) await db.workspace_latest_messages.bulkDelete(staleIds);
}

// Deletes shrink this projection and nothing else refills it: /messages/bootstrap seeds only
// PER_WORKSPACE_DEFAULT (3) rows per workspace, and the destroy paths deliberately skip the
// `workspace_latest_messages` catchup kind. Without a backfill, deleting a short burst of recent
// messages empties a workspace's slice, and the rail loses both its preview text and its activity
// sort key (railItemActivity falls back to workspace.created_at, sinking the row to the bottom)
// until the next incoming message or a reload.
//
// Read both durable message stores. A channel can be owned entirely by Echo, while cold-start
// bootstrap rows still live in the legacy table.
async function backfillWorkspace(workspaceId, isDeleted) {
  const presentIds = await db.workspace_latest_messages.where('workspace_id').equals(workspaceId).primaryKeys();
  if (presentIds.length >= WORKSPACE_LATEST_MESSAGE_LIMIT) return;

  const present = new Set(presentIds);
  const candidateLimit = WORKSPACE_LATEST_MESSAGE_LIMIT * 2;
  const [feedCandidates, feedMetadata] = await Promise.all([
    newestFirstForWorkspace(db.feed_messages, workspaceId)
      .filter((message) => !isDeleted(message) && isMessagePreviewCandidate(message))
      .limit(candidateLimit)
      .toArray(),
    db.message_feed_meta
      .where('[workspace_id+channel_id]')
      .between([workspaceId, Dexie.minKey], [workspaceId, Dexie.maxKey], false, true)
      .filter((row) => Boolean(row.sync_cursor))
      .toArray(),
  ]);
  const feedOwnedChannelIds = new Set(feedMetadata.map((row) => String(row.channel_id)));
  const legacyCandidates = await newestFirstForWorkspace(db.messages, workspaceId)
    .filter(
      (message) =>
        !feedOwnedChannelIds.has(String(message.channel_id)) && !isDeleted(message) && isMessagePreviewCandidate(message),
    )
    .limit(candidateLimit)
    .toArray();
  const candidates = [...feedCandidates, ...legacyCandidates].sort((a, b) => String(b.sort_order).localeCompare(String(a.sort_order)));

  // Never overwrite a row already in the projection: the feed replicator writes previews that
  // db.messages may not carry, and a live edit can land here before it reaches db.messages.
  const rows = [];
  const seen = new Set(present);
  for (const message of candidates) {
    const row = previewRow(message);
    if (!row || seen.has(row.id)) continue;

    rows.push(row);
    seen.add(row.id);
    if (rows.length >= WORKSPACE_LATEST_MESSAGE_LIMIT - present.size) break;
  }

  if (rows.length > 0) await db.workspace_latest_messages.bulkPut(rows);
}

/**
 * @param {readonly Record<string, any>[]} messages
 * @param {{ workspaceIds?: readonly string[] | null }} options
 */
export async function replaceWorkspaceLatestMessages(messages, { workspaceIds = null } = {}) {
  const rows = messages.map(previewRow).filter(Boolean);
  const scopedWorkspaceIds = workspaceIds ? [...new Set(workspaceIds.filter(Boolean).map(String))] : null;

  await db.transaction('rw', db.workspace_latest_messages, async () => {
    if (scopedWorkspaceIds) {
      await db.workspace_latest_messages.where('workspace_id').anyOf(scopedWorkspaceIds).delete();
    } else {
      await db.workspace_latest_messages.clear();
    }

    if (rows.length > 0) await db.workspace_latest_messages.bulkPut(rows);

    const touchedWorkspaceIds = new Set([...(scopedWorkspaceIds || []), ...rows.map((row) => row.workspace_id)]);
    await Promise.all([...touchedWorkspaceIds].map(trimWorkspace));
  });
}

/**
 * `deletes` remove projection rows after Echo reconciliation without crossing store ownership.
 * `confirmedDeletes` are authoritative server deletes and purge both durable message stores.
 *
 * @param {{ upserts?: readonly Record<string, any>[], deletes?: readonly Record<string, any>[], confirmedDeletes?: readonly Record<string, any>[] }} changes
 */
export async function applyWorkspaceLatestMessageChanges({ upserts = [], deletes = [], confirmedDeletes = [] } = {}) {
  const confirmedDeleteIds = new Set(confirmedDeletes.map((message) => message?.id).filter(Boolean).map(String));
  const deleteIds = new Set([...deletes, ...confirmedDeletes].map((message) => message?.id).filter(Boolean).map(String));
  const upsertPreviews = upserts.map((message) => ({ message, row: previewRow(message) }));
  const rows = upsertPreviews.map(({ row }) => row).filter((row) => row && !deleteIds.has(row.id));
  const ineligibleUpsertIds = new Set(
    upsertPreviews.filter(({ message, row }) => message?.id && !row).map(({ message }) => String(message.id)),
  );
  const projectionDeleteIds = new Set([...deleteIds, ...ineligibleUpsertIds]);
  const deletedWorkspaceIds = new Set(
    [...deletes, ...confirmedDeletes, ...upsertPreviews.filter(({ row }) => !row).map(({ message }) => message)]
      .map((message) => message?.workspace_id)
      .filter(Boolean)
      .map(String),
  );

  if (rows.length === 0 && projectionDeleteIds.size === 0) return;

  await db.transaction('rw', db.workspace_latest_messages, db.messages, db.feed_messages, db.message_feed_meta, async () => {
    if (projectionDeleteIds.size > 0) {
      // Take the workspace from the stored row rather than trusting the caller's payload —
      // an echo feed row and a REST message row reach here with different shapes, and a delete
      // that can't name its workspace would deplete the projection with no backfill.
      const storedRows = await db.workspace_latest_messages.bulkGet([...projectionDeleteIds]);
      storedRows.forEach((row) => row?.workspace_id && deletedWorkspaceIds.add(row.workspace_id));
      await db.workspace_latest_messages.bulkDelete([...projectionDeleteIds]);
    }
    if (confirmedDeleteIds.size > 0) {
      const ids = [...confirmedDeleteIds];
      const [legacyRows, feedRows] = await Promise.all([db.messages.bulkGet(ids), db.feed_messages.bulkGet(ids)]);
      [...legacyRows, ...feedRows].forEach((row) => row?.workspace_id && deletedWorkspaceIds.add(String(row.workspace_id)));
      await Promise.all([db.messages.bulkDelete(ids), db.feed_messages.bulkDelete(ids)]);
    }
    if (rows.length > 0) await db.workspace_latest_messages.bulkPut(rows);

    await Promise.all(
      [...deletedWorkspaceIds].map((workspaceId) => backfillWorkspace(workspaceId, (message) => projectionDeleteIds.has(String(message.id)))),
    );

    const touchedWorkspaceIds = new Set([...deletedWorkspaceIds, ...rows.map((row) => row.workspace_id)]);
    await Promise.all([...touchedWorkspaceIds].map(trimWorkspace));
  });
}

export async function deleteWorkspaceLatestMessageBySortOrder(workspaceId, channelId, sortOrder) {
  if (!workspaceId || !channelId || !sortOrder) return;

  const scopedWorkspaceId = String(workspaceId);
  const scopedChannelId = String(channelId);
  const isDeleted = (message) => String(message.channel_id) === scopedChannelId && message.sort_order === sortOrder;

  await db.transaction('rw', db.workspace_latest_messages, db.messages, db.feed_messages, db.message_feed_meta, async () => {
    const projectionRows = await db.workspace_latest_messages
      .where('[workspace_id+sort_order]')
      .equals([scopedWorkspaceId, sortOrder])
      .filter(isDeleted)
      .toArray();
    const legacyRows = await db.messages.where('[channel_id+sort_order]').equals([scopedChannelId, sortOrder]).filter(isDeleted).toArray();
    const feedRows = await db.feed_messages.where('[channel_id+sort_order]').equals([scopedChannelId, sortOrder]).filter(isDeleted).toArray();
    const deleteIds = [...new Set([...projectionRows, ...legacyRows, ...feedRows].map((message) => String(message.id)))];

    if (deleteIds.length === 0) return;

    await Promise.all([
      db.workspace_latest_messages.bulkDelete(deleteIds),
      db.messages.bulkDelete(deleteIds),
      db.feed_messages.bulkDelete(deleteIds),
    ]);
    await backfillWorkspace(scopedWorkspaceId, isDeleted);
  });
}

export async function syncWorkspaceLatestMessageChanges(changes) {
  try {
    await applyWorkspaceLatestMessageChanges(changes);
  } catch (error) {
    logError('workspace-latest-messages', 'Failed to update the workspace rail projection', error);
    window.dispatchEvent(new CustomEvent(WORKSPACE_LATEST_MESSAGES_REFRESH_EVENT));
  }
}

export function purgeWorkspaceLatestMessages(workspaceId) {
  if (!workspaceId) return Promise.resolve();
  return db.workspace_latest_messages.where('workspace_id').equals(String(workspaceId)).delete();
}
